import type { ReactNode } from "react";
import { CheckCircle2, Clock, Mail, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { WeatherCard } from "@/components/WeatherCard";
import { FlightsCard } from "@/components/briefing/FlightsCard";
import { VideoExamplesCard } from "@/components/briefing/VideoExamplesCard";
import { wizardStagesForDay } from "@/lib/briefingStages";
import type { BriefingStage } from "@/types/domain";
import type { CrewMemberFormData } from "@/types/forms";
import type { ProfileRow } from "@/types/supabase-helpers";
import {
  formatDate,
  formatDayLabel,
  type BriefingCustomField,
  type BriefingVideoExample,
  type DayDoors,
  type DoorsWindow,
} from "@produce/shared";

/**
 * The wizard's final step ("Review & publish"): a read-only recap of
 * everything the producer entered, straight before publishing.
 *
 * Extracted from CreateEditBriefing so this screen can be rendered and
 * asserted on in isolation. The editor page owns the state; this
 * component only renders it — which is what makes a missing section
 * (flights, for years) something a test can catch instead of something
 * a producer notices on the day of the show.
 *
 * The recap deliberately renders the same shared cards the other
 * producer surfaces use (WeatherCard, FlightsCard) rather than
 * re-implementing them, so the review step cannot drift away from the
 * published view, the crew app and the PDF.
 *
 * Why so many props: the recap must show the editor's CURRENT form
 * state, unsaved edits included, so it cannot fetch the briefing row
 * for itself. WeatherCard and FlightsCard are the exceptions — those
 * two persist on their own schedule, so they take a briefing id and
 * read their own data.
 */

interface ReviewMeal {
  label: string;
  enabled: boolean;
  start: string;
  end: string;
}

export interface ReviewPublishStepProps {
  /** Briefing id; null while a brand-new production is still unsaved. */
  briefingId: string | null | undefined;
  festivalName: string;
  clientName: string;
  location: string;
  navigationAddress: string;
  hotelAddress: string;
  crewCheckin: string;
  festivalDays: { date: string; startTime: string; endTime: string; name?: string }[];
  /** Per-day doors overrides, for the weather preview. */
  dayDoors: DayDoors[];
  /** Single-day fallback doors, for the weather preview. */
  fallbackDoors: DoorsWindow;
  stages: BriefingStage[];
  producerProfile:
    | Pick<ProfileRow, "company_name" | "full_name" | "phone" | "email" | "invoice_information">
    | null
    | undefined;
  crewDresscode: string;
  /** Catering windows, in the order they are shown. */
  meals: ReviewMeal[];
  /** Producer toggle for the flights section. */
  flightsEnabled: boolean;
  cameraSettings: string;
  audioSettings: string;
  extraSettings: string;
  restrictions: string;
  generalInstructions: string;
  others: string;
  customFields: BriefingCustomField[];
  videoExamples: BriefingVideoExample[];
  crew: Pick<CrewMemberFormData, "name" | "function" | "event_date" | "arrival_time">[];
  /** Selected day on the per-day sections; owned by the editor page. */
  selectedDayIndex: number;
  /**
   * Renders a time honouring the producer's 12h/24h setting.
   *
   * A prop rather than the hook: this component is rendered bare in its unit
   * test, so reaching for producer settings here would drag a query client and
   * an auth provider into every one of those tests. The parent already has the
   * formatter — see how the schedule components take it.
   */
  formatTime: (time: string) => string;
  onSelectDay: (index: number) => void;
}

/** One section of the recap: icon, heading, content. */
function RecapCard({
  title,
  icon: Icon = CheckCircle2,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
}) {
  return (
    <Card className="bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h3 className="text-xl font-semibold">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

/**
 * One label/value pair. `multiline` keeps the producer's own line
 * breaks — used for the free-text blobs (settings, restrictions,
 * instructions), not for the short single-line fields.
 */
function Field({
  label,
  value,
  fallback = "Not set",
  multiline = false,
  className,
}: {
  label: string;
  value: ReactNode;
  fallback?: string;
  multiline?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <h4 className="mb-2 font-semibold">{label}</h4>
      <p className={cn("text-muted-foreground", multiline && "whitespace-pre-wrap")}>
        {value || fallback}
      </p>
    </div>
  );
}

/**
 * Day 1 / Day 2 / Day 3 selector for the multi-day review step. The
 * editor keys its per-day data by array index (stage names "day{i}_…",
 * festivalDays[i]), so this selects by index rather than by date like
 * the published-view DayTabs. Renders nothing for single-day events.
 */
function ReviewDayButtons({
  days,
  selectedIndex,
  onSelect,
}: {
  days: { date: string; name?: string }[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  if (days.length <= 1) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {days.map((day, index) => (
        <Button
          key={index}
          type="button"
          size="sm"
          variant={index === selectedIndex ? "default" : "outline"}
          onClick={() => onSelect(index)}
        >
          {formatDayLabel({ index, customName: day.name })}
        </Button>
      ))}
    </div>
  );
}

export function ReviewPublishStep({
  briefingId,
  festivalName,
  clientName,
  location,
  navigationAddress,
  hotelAddress,
  crewCheckin,
  festivalDays,
  dayDoors,
  fallbackDoors,
  stages,
  producerProfile,
  crewDresscode,
  meals,
  flightsEnabled,
  cameraSettings,
  audioSettings,
  extraSettings,
  restrictions,
  generalInstructions,
  others,
  customFields,
  videoExamples,
  crew,
  selectedDayIndex,
  formatTime,
  onSelectDay,
}: ReviewPublishStepProps) {
  const isMultiDay = festivalDays.length > 1;
  const dayIndex = Math.min(selectedDayIndex, Math.max(festivalDays.length - 1, 0));
  const selectedDate = festivalDays[dayIndex]?.date;

  // Per-day so a stage set for 3 days no longer appears 3×.
  const dayStages = wizardStagesForDay(stages, dayIndex);

  // Per-day so a long multi-day crew list is split by day.
  const crewRows = (isMultiDay ? crew.filter((m) => m.event_date === selectedDate) : crew)
    .slice()
    .sort((a, b) => {
      const dateCompare = a.event_date.localeCompare(b.event_date);
      if (dateCompare !== 0) return dateCompare;
      return (a.arrival_time || "").localeCompare(b.arrival_time || "");
    });

  return (
    <div className="space-y-6">
      <RecapCard title="General information">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Festival name" value={festivalName} />
          <Field label="Client name" value={clientName} />
          <Field label="Location" value={location} />
          <Field label="Navigation address" value={navigationAddress} />
          <Field label="Hotel address" value={hotelAddress} />
          <Field
            label="Date range"
            value={
              festivalDays.length > 0
                ? `${festivalDays[0].date} to ${festivalDays[festivalDays.length - 1].date}`
                : ""
            }
          />
          <div>
            <h4 className="mb-2 font-semibold">Doors open / close</h4>
            {festivalDays.length === 0 ? (
              <p className="text-muted-foreground">Not set</p>
            ) : festivalDays.length === 1 ? (
              <p className="text-muted-foreground">
                {formatTime(festivalDays[0].startTime)} - {formatTime(festivalDays[0].endTime)}
              </p>
            ) : (
              <ul className="space-y-1 text-muted-foreground">
                {festivalDays.map((d, i) => (
                  <li key={d.date}>
                    Day {i + 1} ({d.date}): {formatTime(d.startTime)} - {formatTime(d.endTime)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Field label="Crew check-in information" value={crewCheckin} />
        </div>
      </RecapCard>

      {/* Weather forecast (read-only preview; no write side-effect) */}
      <WeatherCard
        briefingId={briefingId}
        hasLocation={!!navigationAddress}
        enableRefresh={false}
        perDayDoors={dayDoors}
        fallbackDoors={fallbackDoors}
      />

      <RecapCard title="Stages overview">
        {stages.length === 0 ? (
          <p className="text-muted-foreground">No stages added</p>
        ) : (
          <div className="space-y-4">
            <ReviewDayButtons days={festivalDays} selectedIndex={dayIndex} onSelect={onSelectDay} />
            {dayStages.length === 0 ? (
              <p className="text-muted-foreground">No stages for this day</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {dayStages.map((stage, idx) => (
                  <div key={idx} className="rounded-lg border border-border p-3">
                    <p className="font-medium">{stage.label}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-sm text-muted-foreground">
              <span className="font-semibold">Total stages{isMultiDay ? " this day" : ""}:</span>{" "}
              {dayStages.length}
            </p>
          </div>
        )}
      </RecapCard>

      <RecapCard title="Producer information">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Company name" value={producerProfile?.company_name} />
          <Field label="Producer name" value={producerProfile?.full_name} />
          <Field label="Phone number" value={producerProfile?.phone} />
          <Field label="Email" value={producerProfile?.email} />
          {producerProfile?.invoice_information && (
            <Field
              label="Invoice information"
              value={producerProfile.invoice_information}
              multiline
              className="col-span-2"
            />
          )}
        </div>
      </RecapCard>

      <RecapCard title="Crew information">
        <Field label="Crew dresscode" value={crewDresscode} multiline />
      </RecapCard>

      <RecapCard title="Catering">
        <div className="grid grid-cols-3 gap-4">
          {meals.map((meal) => (
            <Field
              key={meal.label}
              label={meal.label}
              value={meal.enabled ? `${meal.start} - ${meal.end}` : ""}
              fallback="Not provided"
            />
          ))}
        </div>
      </RecapCard>

      {/* Flights, grouped per crew member — the same read-only card the
          published view, the crew app and the PDF render. Hides itself
          when the section is off or nobody has a flight yet. */}
      <FlightsCard briefingId={briefingId} enabled={flightsEnabled} />

      <RecapCard title="Technical settings">
        <div className="space-y-4">
          <Field label="Camera settings" value={cameraSettings} multiline />
          <Field label="Audio settings" value={audioSettings} multiline />
          <Field label="Extra settings" value={extraSettings} multiline />
        </div>
      </RecapCard>

      <RecapCard title="Restrictions & instructions">
        <div className="space-y-4">
          <Field label="Restrictions" value={restrictions} fallback="No restrictions" multiline />
          <Field label="General instructions" value={generalInstructions} multiline />
          {others && <Field label="Other notes" value={others} multiline />}
        </div>
      </RecapCard>

      <VideoExamplesCard videoExamples={videoExamples} className="mb-0 p-6" />

      {customFields.length > 0 && (
        <RecapCard title="Additional information">
          <div className="space-y-4">
            {customFields.map((f) => (
              // A field may carry a title with no content yet: show it
              // empty rather than claiming it was "Not set".
              <Field
                key={f.id}
                label={f.title || "Untitled"}
                value={f.content}
                fallback=""
                multiline
              />
            ))}
          </div>
        </RecapCard>
      )}

      <RecapCard title="Crew arrival times" icon={Clock}>
        {crew.length === 0 ? (
          <p className="text-muted-foreground">No crew members added yet</p>
        ) : (
          <div className="space-y-4">
            <ReviewDayButtons days={festivalDays} selectedIndex={dayIndex} onSelect={onSelectDay} />
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Function</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Arrival time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {crewRows.map((member, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>{member.function}</TableCell>
                      <TableCell>{formatDate(member.event_date, "EEEE, MMMM d, yyyy")}</TableCell>
                      <TableCell>{formatTime(member.arrival_time) || "Not set"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </RecapCard>

      {crew.length > 0 && (
        <Card className="bg-card p-6">
          <div className="mb-2 flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <h3 className="text-xl font-semibold">Crew invitations</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            After publishing, you'll be redirected to invite all {crew.length} crew members. They
            will receive an email with instructions to access their personalized schedule and
            briefing.
          </p>
        </Card>
      )}
    </div>
  );
}
